// src/components/admin/LocalityAdminsTab.jsx
// Admin panel tab for managing Steward (and future Peacemaker) assignments.
// Superadmin can search users, pick a boundary, select a role, and grant/revoke.

import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../themes/ThemeProvider';
import { useTranslation } from '../../i18n/useTranslation';
import { supabaseFetch } from '../../lib/supabase';
import { getAllLocalityAdmins, grantLocalityAdmin, revokeLocalityAdmin } from '../../data/directoryService';
import { getUserLocalityAdminBoundaries } from '../../utils/adminHelpers';
import ConfirmationModal from '../core/ConfirmationModal';

const ROLES = [
  { value: 'steward', enabled: true },
  { value: 'peacemaker', enabled: false }
];

const LocalityAdminsTab = ({ currentUser }) => {
  const theme = useTheme();
  const { t } = useTranslation('admin');

  const [loading, setLoading] = useState(true);
  const [userSearch, setUserSearch] = useState('');
  const [userResults, setUserResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState('steward');
  const [existingStewardships, setExistingStewardships] = useState(null);
  const [boundarySearch, setBoundarySearch] = useState('');
  const [boundaryResults, setBoundaryResults] = useState([]);
  const [searchingBoundary, setSearchingBoundary] = useState(false);
  const [selectedBoundary, setSelectedBoundary] = useState(null);
  const [granting, setGranting] = useState(false);
  const [revokeModal, setRevokeModal] = useState({ visible: false, admin: null });
  const [enrichedAdmins, setEnrichedAdmins] = useState([]);
  const [message, setMessage] = useState(null);

  // Load current locality admins
  const loadAdmins = useCallback(async () => {
    setLoading(true);
    const data = await getAllLocalityAdmins();

    // Enrich with user display names and boundary names
    if (data.length > 0) {
      const userIds = [...new Set(data.map(a => a.user_id))];
      const boundaryIds = [...new Set(data.map(a => a.boundary_id))];
      const grantedByIds = [...new Set(data.map(a => a.granted_by))];
      const allUserIds = [...new Set([...userIds, ...grantedByIds])];

      const [usersRes, boundariesRes] = await Promise.all([
        supabaseFetch.query('users', {
          select: 'id,display_name,email',
          inFilter: { id: allUserIds }
        }),
        supabaseFetch.rpc('get_boundaries_by_ids', { p_ids: boundaryIds })
      ]);

      const userMap = {};
      (usersRes.data || []).forEach(u => { userMap[u.id] = u; });
      const boundaryMap = {};
      (boundariesRes.data || []).forEach(b => { boundaryMap[b.id] = b; });

      const enriched = data.map(a => ({
        ...a,
        user_display_name: userMap[a.user_id]?.display_name || t('steward_unknownUser'),
        user_email: userMap[a.user_id]?.email || '',
        boundary_name: boundaryMap[a.boundary_id]?.name || `#${a.boundary_id}`,
        granted_by_name: userMap[a.granted_by]?.display_name || ''
      }));
      setEnrichedAdmins(enriched);
    } else {
      setEnrichedAdmins([]);
    }

    setLoading(false);
  }, [t]);

  useEffect(() => {
    loadAdmins();
  }, [loadAdmins]);

  // When a user is selected, load their existing stewardships
  useEffect(() => {
    if (!selectedUser) {
      setExistingStewardships(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const assignments = await getUserLocalityAdminBoundaries(selectedUser.id, 'steward');
      if (cancelled) return;
      if (assignments.length === 0) {
        setExistingStewardships([]);
        return;
      }
      const boundaryIds = assignments.map(a => a.boundary_id);
      const { data } = await supabaseFetch.rpc('get_boundaries_by_ids', { p_ids: boundaryIds });
      if (cancelled) return;
      setExistingStewardships((data || []).map(b => b.name));
    })();
    return () => { cancelled = true; };
  }, [selectedUser]);

  // Search users
  const handleUserSearch = async () => {
    if (!userSearch.trim() || userSearch.trim().length < 2) return;
    setSearching(true);
    const { data } = await supabaseFetch.query('users', {
      select: 'id,display_name,email',
      ilike: { display_name: `%${userSearch.trim()}%` },
      limit: 10
    });
    setUserResults(data || []);
    setSearching(false);
  };

  // Search boundaries (localities)
  const handleBoundarySearch = async () => {
    if (!boundarySearch.trim() || boundarySearch.trim().length < 2) return;
    setSearchingBoundary(true);
    const { data } = await supabaseFetch.rpc('search_boundaries', {
      p_search_term: boundarySearch.trim(),
      p_admin_level: null,
      p_limit: 10
    });
    setBoundaryResults(data || []);
    setSearchingBoundary(false);
  };

  // Grant
  const handleGrant = async () => {
    if (!selectedUser || !selectedBoundary || selectedRole === 'peacemaker') return;
    setGranting(true);
    setMessage(null);
    const result = await grantLocalityAdmin(selectedUser.id, selectedBoundary.id, selectedRole);
    setGranting(false);
    if (result.success) {
      setMessage({ type: 'success', text: t('steward_granted', { user: selectedUser.display_name, boundary: selectedBoundary.name }) });
      setSelectedUser(null);
      setSelectedBoundary(null);
      setSelectedRole('steward');
      setUserSearch('');
      setBoundarySearch('');
      setUserResults([]);
      setBoundaryResults([]);
      loadAdmins();
    } else {
      setMessage({ type: 'error', text: result.error?.message || t('steward_grantError') });
    }
  };

  // Revoke
  const handleRevoke = async () => {
    const admin = revokeModal.admin;
    if (!admin) return;
    setRevokeModal({ visible: false, admin: null });
    const result = await revokeLocalityAdmin(admin.user_id, admin.boundary_id, admin.role);
    if (result.success) {
      setMessage({ type: 'success', text: t('steward_revoked') });
      loadAdmins();
    } else {
      setMessage({ type: 'error', text: result.error?.message || t('steward_revokeError') });
    }
  };

  const inputStyle = {
    flex: 1,
    padding: '8px 12px',
    border: `1px solid ${theme?.border || '#ddd'}`,
    borderRadius: '6px',
    fontFamily: 'OpenSauceSans, sans-serif',
    fontSize: '13px',
    outline: 'none'
  };

  const btnStyle = {
    padding: '8px 16px',
    border: 'none',
    borderRadius: '6px',
    fontFamily: 'OpenSauceSans, sans-serif',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer'
  };

  const roleLabel = (role) => {
    if (role === 'steward') return t('steward_roleSteward');
    if (role === 'peacemaker') return t('steward_rolePeacemaker');
    return role;
  };

  return (
    <div style={{ maxWidth: '800px' }}>
      <h2 style={{
        fontSize: '18px',
        fontWeight: '700',
        color: theme?.text?.primary || '#1f2937',
        fontFamily: 'OpenSauceSans, sans-serif',
        marginBottom: '16px'
      }}>
        {t('tabStewards')}
      </h2>

      {/* Message banner */}
      {message && (
        <div style={{
          padding: '10px 14px',
          marginBottom: '16px',
          borderRadius: '8px',
          fontFamily: 'OpenSauceSans, sans-serif',
          fontSize: '13px',
          background: message.type === 'success' ? '#ecfdf5' : '#fef2f2',
          color: message.type === 'success' ? '#059669' : '#dc2626',
          border: `1px solid ${message.type === 'success' ? '#a7f3d0' : '#fecaca'}`
        }}>
          {message.text}
        </div>
      )}

      {/* Grant section */}
      <div style={{
        background: 'white',
        borderRadius: '10px',
        padding: '16px',
        marginBottom: '24px',
        border: `1px solid ${theme?.border || '#e5e7eb'}`
      }}>
        <h3 style={{
          fontSize: '14px',
          fontWeight: '700',
          color: theme?.text?.primary || '#1f2937',
          fontFamily: 'OpenSauceSans, sans-serif',
          marginTop: 0,
          marginBottom: '12px'
        }}>
          {t('steward_grantTitle')}
        </h3>

        {/* User search */}
        <div style={{ marginBottom: '12px' }}>
          <label style={{
            fontFamily: 'OpenSauceSans, sans-serif',
            fontSize: '12px',
            fontWeight: '600',
            color: '#6b7280',
            display: 'block',
            marginBottom: '4px'
          }}>
            {t('steward_selectUser')}
          </label>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              type="text"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleUserSearch()}
              placeholder={t('steward_searchUserPlaceholder')}
              style={inputStyle}
            />
            <button
              onClick={handleUserSearch}
              disabled={searching}
              style={{ ...btnStyle, background: theme?.primary || '#024B99', color: 'white' }}
            >
              {t('common:search')}
            </button>
          </div>
          {/* User results */}
          {userResults.length > 0 && !selectedUser && (
            <div style={{ marginTop: '6px', maxHeight: '150px', overflowY: 'auto' }}>
              {userResults.map(u => (
                <button
                  key={u.id}
                  onClick={() => { setSelectedUser(u); setUserResults([]); }}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '6px 10px',
                    border: 'none',
                    background: 'none',
                    fontFamily: 'OpenSauceSans, sans-serif',
                    fontSize: '13px',
                    cursor: 'pointer',
                    borderRadius: '4px'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                >
                  <strong>{u.display_name}</strong>
                  <span style={{ color: '#9ca3af', marginLeft: '8px', fontSize: '12px' }}>{u.email}</span>
                </button>
              ))}
            </div>
          )}
          {selectedUser && (
            <div style={{
              marginTop: '6px',
              padding: '6px 10px',
              background: '#eff6ff',
              borderRadius: '6px',
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <span><strong>{selectedUser.display_name}</strong> ({selectedUser.email})</span>
              <button
                onClick={() => setSelectedUser(null)}
                style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '16px' }}
              >
                x
              </button>
            </div>
          )}
          {/* Existing stewardships info line */}
          {selectedUser && existingStewardships !== null && (
            <div style={{
              marginTop: '4px',
              padding: '4px 10px',
              fontFamily: 'OpenSauceSans, sans-serif',
              fontSize: '12px',
              color: '#6b7280'
            }}>
              {existingStewardships.length > 0
                ? t('steward_alreadyStewards', {
                    list: existingStewardships.join(', '),
                    count: String(existingStewardships.length)
                  })
                : t('steward_noneYet')
              }
            </div>
          )}
        </div>

        {/* Role selector */}
        <div style={{ marginBottom: '12px' }}>
          <label style={{
            fontFamily: 'OpenSauceSans, sans-serif',
            fontSize: '12px',
            fontWeight: '600',
            color: '#6b7280',
            display: 'block',
            marginBottom: '4px'
          }}>
            {t('steward_roleLabel')}
          </label>
          <div style={{ display: 'flex', gap: '8px' }}>
            {ROLES.map(r => (
              <button
                key={r.value}
                onClick={() => r.enabled && setSelectedRole(r.value)}
                disabled={!r.enabled}
                style={{
                  ...btnStyle,
                  padding: '6px 14px',
                  background: selectedRole === r.value ? (theme?.primary || '#024B99') : '#f3f4f6',
                  color: selectedRole === r.value ? 'white' : (r.enabled ? '#374151' : '#9ca3af'),
                  opacity: r.enabled ? 1 : 0.6,
                  cursor: r.enabled ? 'pointer' : 'not-allowed'
                }}
              >
                {roleLabel(r.value)}
                {!r.enabled && (
                  <span style={{ marginLeft: '6px', fontSize: '11px', fontWeight: '400' }}>
                    ({t('steward_peacemakerComingSoon')})
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Boundary search */}
        <div style={{ marginBottom: '12px' }}>
          <label style={{
            fontFamily: 'OpenSauceSans, sans-serif',
            fontSize: '12px',
            fontWeight: '600',
            color: '#6b7280',
            display: 'block',
            marginBottom: '4px'
          }}>
            {t('steward_selectBoundary')}
          </label>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              type="text"
              value={boundarySearch}
              onChange={(e) => setBoundarySearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleBoundarySearch()}
              placeholder={t('steward_searchBoundaryPlaceholder')}
              style={inputStyle}
            />
            <button
              onClick={handleBoundarySearch}
              disabled={searchingBoundary}
              style={{ ...btnStyle, background: theme?.primary || '#024B99', color: 'white' }}
            >
              {t('common:search')}
            </button>
          </div>
          {/* Boundary results */}
          {boundaryResults.length > 0 && !selectedBoundary && (
            <div style={{ marginTop: '6px', maxHeight: '150px', overflowY: 'auto' }}>
              {boundaryResults.map(b => (
                <button
                  key={b.id}
                  onClick={() => { setSelectedBoundary(b); setBoundaryResults([]); }}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '6px 10px',
                    border: 'none',
                    background: 'none',
                    fontFamily: 'OpenSauceSans, sans-serif',
                    fontSize: '13px',
                    cursor: 'pointer',
                    borderRadius: '4px'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                >
                  {b.name}
                  {b.admin_level && (
                    <span style={{ color: '#9ca3af', marginLeft: '8px', fontSize: '11px' }}>
                      L{b.admin_level}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
          {selectedBoundary && (
            <div style={{
              marginTop: '6px',
              padding: '6px 10px',
              background: '#eff6ff',
              borderRadius: '6px',
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <span><strong>{selectedBoundary.name}</strong></span>
              <button
                onClick={() => setSelectedBoundary(null)}
                style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '16px' }}
              >
                x
              </button>
            </div>
          )}
        </div>

        {/* Grant button */}
        <button
          onClick={handleGrant}
          disabled={!selectedUser || !selectedBoundary || granting || selectedRole === 'peacemaker'}
          style={{
            ...btnStyle,
            background: (!selectedUser || !selectedBoundary || granting || selectedRole === 'peacemaker') ? '#93c5fd' : '#059669',
            color: 'white',
            width: '100%',
            padding: '10px'
          }}
        >
          {granting ? t('steward_granting') : t('steward_grantButton')}
        </button>
      </div>

      {/* Current stewards list */}
      <div style={{
        background: 'white',
        borderRadius: '10px',
        padding: '16px',
        border: `1px solid ${theme?.border || '#e5e7eb'}`
      }}>
        <h3 style={{
          fontSize: '14px',
          fontWeight: '700',
          color: theme?.text?.primary || '#1f2937',
          fontFamily: 'OpenSauceSans, sans-serif',
          marginTop: 0,
          marginBottom: '12px'
        }}>
          {t('steward_currentTitle')} ({enrichedAdmins.length})
        </h3>

        {loading ? (
          <div style={{ padding: '20px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>
            {t('common:loading')}
          </div>
        ) : enrichedAdmins.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>
            {t('steward_none')}
          </div>
        ) : (
          <div>
            {enrichedAdmins.map(admin => (
              <div
                key={admin.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 12px',
                  borderBottom: '1px solid #f3f4f6',
                  fontFamily: 'OpenSauceSans, sans-serif'
                }}
              >
                <div>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#1f2937' }}>
                    {admin.user_display_name}
                    <span style={{ fontWeight: '400', color: '#9ca3af', marginLeft: '8px', fontSize: '12px' }}>
                      {admin.user_email}
                    </span>
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                    {admin.boundary_name}
                    <span style={{
                      marginLeft: '8px',
                      padding: '1px 6px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: '600',
                      background: admin.role === 'steward' ? '#dbeafe' : '#f3e8ff',
                      color: admin.role === 'steward' ? '#1d4ed8' : '#7c3aed'
                    }}>
                      {roleLabel(admin.role)}
                    </span>
                    {admin.granted_by_name && (
                      <span style={{ color: '#9ca3af', marginLeft: '8px' }}>
                        {t('steward_grantedBy')} {admin.granted_by_name}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setRevokeModal({ visible: true, admin })}
                  style={{
                    ...btnStyle,
                    background: '#fef2f2',
                    color: '#dc2626',
                    fontSize: '12px',
                    padding: '6px 12px'
                  }}
                >
                  {t('steward_revoke')}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmationModal
        visible={revokeModal.visible}
        title={t('steward_revokeConfirmTitle')}
        message={revokeModal.admin
          ? t('steward_revokeConfirmMessage', {
              user: revokeModal.admin.user_display_name,
              boundary: revokeModal.admin.boundary_name
            })
          : ''}
        okLabel={t('steward_revoke')}
        cancelLabel={t('common:cancel')}
        danger={true}
        onOk={handleRevoke}
        onCancel={() => setRevokeModal({ visible: false, admin: null })}
      />
    </div>
  );
};

export default LocalityAdminsTab;
